import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, ArrowRight, Sun, Sparkles, Zap, ShieldCheck } from 'lucide-react';
import Confetti from 'react-confetti';
import { useWindowSize } from 'react-use';

const SuccessPage = ({ isDarkMode }) => {
    const navigate = useNavigate();
    const { width, height } = useWindowSize();

    useEffect(() => {
        // Scroll to top on load
        window.scrollTo(0, 0);
    }, []);

    const premiumFeatures = [
        { icon: Zap, text: "Unlimited lifetime access to all sacred rhythms" },
        { icon: Sun, text: "Live solar-lunar precision tracking enabled" },
        { icon: Sparkles, text: "Advanced planetary vedha paths unlocked" },
        { icon: ShieldCheck, text: "Priority rhythm notifications & notifications" }
    ];

    return (
        <div className={`min-h-screen relative flex items-center justify-center overflow-hidden font-sans transition-colors duration-700 ${isDarkMode ? 'bg-[#050505] text-white' : 'bg-[#FCFDFE] text-slate-900'
            }`}>
            {/* Confetti effect for the first few seconds */}
            <Confetti
                width={width}
                height={height}
                recycle={false}
                numberOfPieces={200}
                colors={['#3B82F6', '#F97316', '#8B5CF6', '#10B981']}
                gravity={0.15}
            />

            {/* Premium Background Orbs */}
            <div className="absolute top-[-10%] right-[-10%] w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[120px] pointer-events-none animate-pulse" />
            <div className="absolute bottom-[-10%] left-[-10%] w-[500px] h-[500px] bg-orange-600/10 rounded-full blur-[120px] pointer-events-none animate-pulse" />

            <div className="relative z-10 max-w-xl w-full mx-auto px-6 py-12">
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className={`rounded-[48px] p-10 border-2 text-center transition-all shadow-2xl ${isDarkMode
                            ? 'bg-[#111111]/80 border-white/10 backdrop-blur-xl'
                            : 'bg-white border-slate-100 shadow-blue-500/10'
                        }`}
                >
                    {/* Success Icon */}
                    <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: 'spring', damping: 12, delay: 0.3 }}
                        className="w-24 h-24 bg-gradient-to-tr from-blue-500 to-orange-500 rounded-[32px] flex items-center justify-center mx-auto mb-8 shadow-2xl shadow-orange-500/20"
                    >
                        <CheckCircle className="text-white w-12 h-12" />
                    </motion.div>

                    <motion.h1
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.5 }}
                        className="text-4xl font-black uppercase tracking-tighter leading-none mb-4"
                    >
                        Mastery <br />
                        <span className="bg-gradient-to-r from-blue-500 to-orange-500 bg-clip-text text-transparent">Acknowledged</span>
                    </motion.h1>

                    <motion.p
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.6 }}
                        className={`text-sm font-bold uppercase tracking-widest mb-10 ${isDarkMode ? 'text-gray-500' : 'text-slate-400'}`}
                    >
                        Welcome to the Solar Path. Your spiritual toolkit is now fully expanded.
                    </motion.p>

                    {/* Features List */}
                    <div className="space-y-4 mb-12 text-left">
                        {premiumFeatures.map((feature, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ x: -20, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                transition={{ delay: 0.8 + (idx * 0.1) }}
                                className={`flex items-center space-x-4 p-4 rounded-2xl border transition-all ${isDarkMode ? 'bg-white/5 border-white/5' : 'bg-slate-50 border-slate-100'
                                    }`}
                            >
                                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500/20 to-orange-500/20 flex items-center justify-center flex-shrink-0">
                                    <feature.icon className="w-5 h-5 text-blue-500" />
                                </div>
                                <span className={`text-sm font-black italic tracking-tight ${isDarkMode ? 'text-gray-300' : 'text-slate-700'}`}>
                                    {feature.text}
                                </span>
                            </motion.div>
                        ))}
                    </div>

                    <motion.button
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 1.3 }}
                        onClick={() => navigate('/dashboard')}
                        className="w-full bg-gradient-to-r from-blue-600 to-orange-500 py-6 rounded-[28px] text-white font-black text-lg uppercase tracking-widest flex items-center justify-center space-x-3 shadow-xl shadow-orange-500/25 hover:scale-[1.02] active:scale-[0.98] transition-all group"
                    >
                        <span>Enter Sanctuary</span>
                        <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
                    </motion.button>
                </motion.div>

                <p className={`mt-8 text-center text-[10px] font-black uppercase tracking-[0.2em] ${isDarkMode ? 'text-gray-700' : 'text-slate-300'}`}>
                    Transaction Verified • Secured by Stripe
                </p>
            </div>
        </div>
    );
};

export default SuccessPage;
